import { BAHN_RADIUS, GERADE_HALB, MITTE_X, MITTE_Y, OBERRING_TREPPEN_GERADE_X, OBERRING_TREPPEN_WINKEL_GRAD, TREPPEN_ABSTAND_GERADE, TREPPEN_GERADE_ANZAHL_HALB, TREPPEN_WINKEL_GRAD, TREPPE_HALBBREITE, } from './masse.js';
function spiegeln(abstaende) {
    return [...new Set(abstaende.flatMap((dx) => [MITTE_X - dx, MITTE_X + dx]))].sort((a, b) => a - b);
}
export const UNTERRING_TREPPEN = {
    linien: Array.from({ length: TREPPEN_GERADE_ANZAHL_HALB * 2 + 1 }, (_, i) => MITTE_X + (i - TREPPEN_GERADE_ANZAHL_HALB) * TREPPEN_ABSTAND_GERADE),
    winkelOst: TREPPEN_WINKEL_GRAD,
    winkelWest: TREPPEN_WINKEL_GRAD,
};
export const OBERRING_TREPPEN = {
    linien: spiegeln(OBERRING_TREPPEN_GERADE_X),
    winkelOst: OBERRING_TREPPEN_WINKEL_GRAD,
    winkelWest: OBERRING_TREPPEN_WINKEL_GRAD,
};
/**
 * Lage eines Punkts zu einer Achse — null, wenn er nicht in deren Bereich
 * liegt (andere Gerade, andere Kurve, hinter dem Kurvenmittelpunkt). Die
 * Umlaufrichtung Osten → Sueden → Westen → Norden ist die des wachsenden
 * Winkels; auf der Nordgeraden zeigt sie nach +x, auf der Suedgeraden nach −x.
 */
export function lageZurAchse(px, py, achse) {
    if (achse.typ === 'gerade') {
        if (inKurve(px) || achse.nord !== py < MITTE_Y)
            return null;
        return { d: abstand(px, py), v: (achse.nord ? 1 : -1) * (px - achse.x) };
    }
    if (!inKurve(px) || achse.ost !== px > MITTE_X)
        return null;
    const mx = naechsterStreckenpunkt(px);
    const dx = px - mx;
    const dy = py - MITTE_Y;
    const r = Math.hypot(dx, dy);
    if (r === 0)
        return null;
    const winkel = Math.atan2(dy, dx) - (achse.ost ? 0 : Math.PI);
    const delta = normalisiere(winkel - (achse.grad * Math.PI) / 180);
    if (Math.abs(delta) >= Math.PI / 2)
        return null;
    return { d: r - BAHN_RADIUS, v: r * Math.sin(delta) };
}
/** Achsen aus Kurvenwinkeln (beidseitig, 0 nur einmal) und Geraden-Abstaenden (beide Geraden, beide Seiten). */
export function achsen(winkelOst, winkelWest, geradeX) {
    const liste = [];
    for (const [ost, winkel] of [[true, winkelOst], [false, winkelWest]]) {
        for (const grad of winkel) {
            for (const vorzeichen of grad === 0 ? [1] : [1, -1])
                liste.push({ typ: 'kurve', ost, grad: vorzeichen * grad });
        }
    }
    for (const dx of geradeX) {
        for (const x of new Set([MITTE_X - dx, MITTE_X + dx])) {
            liste.push({ typ: 'gerade', x, nord: true });
            liste.push({ typ: 'gerade', x, nord: false });
        }
    }
    return liste;
}
/**
 * Die Laufbahn als Kurve mit Abstand: eine Strecke von (MITTE_X − S, MITTE_Y)
 * nach (MITTE_X + S, MITTE_Y), um die alles im Abstand BAHN_RADIUS + d liegt.
 * Parallelkurven dieser Form haben ueberall genau Abstand 1 voneinander —
 * deshalb aendert sich `abstand` zwischen Nachbarkacheln um hoechstens 1,
 * und jede Tribuenenreihe ist genau eine Kachel tief.
 */
/** Mittelpunkt einer Kachel in Kachelkoordinaten. */
export function kachelMitte(x, y) {
    return { px: x + 0.5, py: y + 0.5 };
}
/** Naechster Punkt der Mittelstrecke zu (px, py): auf der Geraden oder ein Endpunkt. */
export function naechsterStreckenpunkt(px) {
    return px < MITTE_X - GERADE_HALB ? MITTE_X - GERADE_HALB : px > MITTE_X + GERADE_HALB ? MITTE_X + GERADE_HALB : px;
}
/** Abstand eines Punkts von der Innenkante der Laufbahn (negativ = innen). */
export function abstand(px, py) {
    const sx = naechsterStreckenpunkt(px);
    return Math.hypot(px - sx, py - MITTE_Y) - BAHN_RADIUS;
}
/** Liegt der Punkt in einer der beiden Kurven (statt an einer Geraden)? */
export function inKurve(px) {
    return px < MITTE_X - GERADE_HALB || px > MITTE_X + GERADE_HALB;
}
export function imWestbogen(px) {
    return px < MITTE_X - GERADE_HALB;
}
/**
 * Abstand der Kachel zur naechsten Linie einer Schar: auf den Geraden
 * senkrechte Linien bei `geradenLinien`, in den Kurven Strahlen vom
 * Kurvenmittelpunkt unter den Winkeln `winkelOst` bzw. `winkelWest`
 * (beidseitig der Achse, 0 nur einmal).
 */
export function abstandZurSchar(px, py, schar) {
    if (!inKurve(px)) {
        return Math.min(...schar.linien.map((lx) => Math.abs(px - lx)));
    }
    const mx = naechsterStreckenpunkt(px);
    const dx = px - mx;
    const dy = py - MITTE_Y;
    const r = Math.hypot(dx, dy);
    if (r === 0)
        return Infinity;
    // Winkel zur Achse des Bogens (Osten: +x, Westen: −x)
    const osten = px > MITTE_X;
    const achse = osten ? 0 : Math.PI;
    const winkel = Math.atan2(dy, dx) - achse;
    let bester = Infinity;
    for (const grad of osten ? schar.winkelOst : schar.winkelWest) {
        for (const vorzeichen of grad === 0 ? [1] : [1, -1]) {
            const strahl = (vorzeichen * grad * Math.PI) / 180;
            // Senkrechter Abstand zum Strahl, nur auf dessen Seite
            const delta = normalisiere(winkel - strahl);
            if (Math.abs(delta) < Math.PI / 2)
                bester = Math.min(bester, r * Math.abs(Math.sin(delta)));
        }
    }
    return bester;
}
/** Liegt die Kachel auf einer Treppe der Schar? */
export function aufTreppe(px, py, treppen) {
    return abstandZurSchar(px, py, treppen) <= TREPPE_HALBBREITE;
}
function normalisiere(w) {
    let r = w;
    while (r > Math.PI)
        r -= 2 * Math.PI;
    while (r < -Math.PI)
        r += 2 * Math.PI;
    return r;
}
//# sourceMappingURL=oval.js.map